import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, toAuthUser } from '../services/authApi';
import { useAuthStore } from '../stores/authStore';

/**
 * Switches the active tenant and applies the result atomically: authStore first,
 * then the query cache.
 *
 * Order matters. Every mounted query's `queryKey`/`enabled` closes over
 * `useAuthStore` state (tenantId, permissions) and the axios interceptor reads
 * the bearer token from the store at request time, not from a stale render
 * closure. Updating the store first means the re-render it triggers already
 * carries the new tenant's identity; clearing the query cache second then
 * forces every observer to refetch under that new identity instead of briefly
 * rendering the new tenant's screen with the old tenant's cached rows (or,
 * worse, the reverse order letting an in-flight refetch race out with the old
 * token before the store swap lands).
 *
 * Deliberately `queryClient.getQueryCache().clear()`, not `queryClient.clear()`:
 * the latter also clears the *mutation* cache, which includes this very
 * mutation while it is still inside its own `onSuccess`. Doing that drops the
 * per-call `onSuccess` a caller passes to `mutate()` (`MutationObserver` only
 * fires it while the mutation is still tracked in the cache) — measured via
 * `TenantSwitcherMenu.test.tsx`'s post-switch navigation assertion, which
 * silently never fired until this was narrowed to the query cache only. Only
 * tenant-scoped *data* needs to disappear on a switch; the mutation cache is
 * unrelated bookkeeping.
 *
 * Errors are surfaced via the shared mutation-error toast channel
 * (`mutationErrorChannel.ts`) — `extractApiError` already translates the
 * `auth.switchTenant.membership.notFound` / `errors.generic.unauthorized`
 * keys the server returns, so no bespoke error mapping is needed here.
 */
export function useSwitchTenant() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: string) => authApi.switchTenant(tenantId),
    onSuccess: (data) => {
      setAuth(data.accessToken, toAuthUser(data));
      queryClient.getQueryCache().clear();
    },
    meta: { errorToast: true },
  });
}
